/** Server status enumeration matching backend schema. */
export enum ServerStatus {
  PENDING = "pending",
  ONLINE = "online",
  DEGRADED = "degraded",
  DOWN = "down",
  OFFLINE = "offline",
  DRAINING = "draining",
}

/** Server type enumeration. */
export enum ServerType {
  KAMAILIO = "kamailio",
  FREESWITCH = "freeswitch",
  CGRATES = "cgrates",
}

/** Server metrics from heartbeat data. */
export interface ServerMetrics {
  cpu_percent: number;
  memory_percent: number;
  active_calls: number;
  load_cps: number;
  load_cc: number;
}

/** Base server interface with common fields. */
export interface ServerBase {
  hostname: string;
  server_type: ServerType;
}

/** Server creation request payload. */
export interface ServerCreate extends ServerBase {}

/** Desired-state drift report from the agent convergence loop (Phase 2.4). */
export interface DriftReport {
  artifact: string;
  expected_sha256?: string;
  actual_sha256?: string;
  drifted?: boolean;
  applied?: boolean;
  verified?: boolean;
  checked_at?: string;
  error?: string;
}

/** Server response from API with all fields. */
export interface Server extends ServerBase {
  id: string;
  status: ServerStatus;
  last_heartbeat: string | null;
  config_version: number;
  cpu_percent: number | null;
  memory_percent: number | null;
  active_calls: number;
  last_drift?: DriftReport | null;
  created_at: string;
  updated_at: string;
}

/** Server response including join token (only on creation). */
export interface ServerWithJoinToken extends Server {
  join_token: string;
}

/** Authentication request payload. */
export interface LoginRequest {
  username: string;
  password: string;
}

/** Authentication response with JWT token. */
export interface LoginResponse {
  access_token: string;
  token_type: string;
}

/** API error response. */
export interface ApiError {
  detail: string;
}

/** Join script response. */
export interface JoinScriptResponse {
  script: string;
  server_id: string;
  hostname: string;
  server_type: string;
}

/** Carrier status enumeration matching backend schema. */
export enum CarrierStatus {
  ACTIVE = "active",
  DISABLED = "disabled",
  SUSPENDED = "suspended",
}

/** Base carrier interface with common fields. */
export interface CarrierBase {
  name: string;
  hostname: string;
  ip_whitelist: string[];
  status: CarrierStatus;
  capacity_cps: number;
  capacity_cc: number;
  priority: number;
}

/** Carrier creation request payload. */
export interface CarrierCreate extends CarrierBase {}

/** Carrier update request payload (all optional). */
export interface CarrierUpdate {
  name?: string;
  hostname?: string;
  ip_whitelist?: string[];
  status?: CarrierStatus;
  capacity_cps?: number;
  capacity_cc?: number;
  priority?: number;
}

/** Carrier response from API with all fields. */
export interface Carrier extends CarrierBase {
  id: string;
  active_calls: number;
  created_at: string;
  updated_at: string;
}

/** Supplier status enumeration matching backend schema. */
export enum SupplierStatus {
  ACTIVE = "ACTIVE",
  DISABLED = "DISABLED",
  SUSPENDED = "SUSPENDED",
}

/** Supplier gateway health status. */
export enum GatewayHealthStatus {
  UNKNOWN = "unknown",
  HEALTHY = "healthy",
  DEGRADED = "degraded",
  DOWN = "down",
}

/** Supplier gateway summary for list views. */
export interface SupplierGatewaySummary {
  id: number;
  ip_address: string;
  port: number;
  enabled: boolean;
  health_status: string;
  priority: number;
  load_share: number;
  hostname?: string;
  auth_type: string;
  username?: string;
  password?: string;
  capacity_cps: number;
  capacity_cc: number;
  inbound_codecs?: string;
  outbound_codecs?: string;
  // RT-4: number translation / ANI (backend SupplierGatewayResponse now
  // returns the FULL gateway — these were stripped by the old summary schema,
  // which crashed the edit handler on undefined reads)
  strip_digits?: number;
  prepend_prefix?: string;
  ani_rewrite?: string;
}

/** Base supplier interface with common fields. */
export interface SupplierBase {
  name: string;
  hostname: string;
  status: SupplierStatus;
  capacity_cps: number;
  capacity_cc: number;
  priority: number;
  cgr_subject?: string;
  tariff_plan: string;
}

/** Supplier creation request payload. */
export interface SupplierCreate extends SupplierBase {}

/** Supplier form data interface (used by UI components). */
export interface SupplierData {
  id?: string;
  name: string;
  hostname: string;
  status: "ACTIVE" | "DISABLED" | "SUSPENDED";
  capacity_cps: number;
  capacity_cc: number;
  priority: number;
  cgr_subject?: string;
  tariff_plan: string;
}

/** Supplier update request payload (all optional). */
export interface SupplierUpdate {
  name?: string;
  hostname?: string;
  status?: SupplierStatus;
  capacity_cps?: number;
  capacity_cc?: number;
  priority?: number;
  cgr_subject?: string;
  tariff_plan?: string;
}

/** Supplier response from API with all fields. */
export interface Supplier extends SupplierBase {
  id: string;
  active_calls: number;
  created_at: string;
  updated_at: string;
  gateways: SupplierGatewaySummary[];
}

/** Supplier list response with pagination. */
export interface SupplierList {
  items: Supplier[];
  total: number;
  page: number;
  size: number;
}

/** Full supplier gateway details. */
export interface SupplierGateway {
  id: number;
  supplier_id: string;
  hostname?: string;
  ip_address: string;
  port: number;
  auth_type: string;
  username?: string;
  password?: string;
  load_share: number;
  priority: number;
  capacity_cps: number;
  capacity_cc: number;
  inbound_codecs?: string;
  outbound_codecs?: string;
  enabled: boolean;
  health_status: string;
  last_check?: string;
  created_at: string;
  updated_at: string;
}

/** Supplier gateway creation request. */
export interface SupplierGatewayCreate {
  hostname?: string;
  ip_address: string;
  port?: number;
  auth_type?: string;
  username?: string;
  password?: string;
  load_share?: number;
  priority?: number;
  capacity_cps?: number;
  capacity_cc?: number;
  enabled?: boolean;
  inbound_codecs?: string;
  outbound_codecs?: string;
  // RT-4: number translation / ANI
  strip_digits?: number;
  prepend_prefix?: string;
  ani_rewrite?: string;
}

/** Supplier gateway update request (all optional). */
export interface SupplierGatewayUpdate {
  hostname?: string;
  ip_address?: string;
  port?: number;
  auth_type?: string;
  username?: string;
  password?: string;
  load_share?: number;
  priority?: number;
  capacity_cps?: number;
  capacity_cc?: number;
  enabled?: boolean;
  inbound_codecs?: string;
  outbound_codecs?: string;
  strip_digits?: number;
  prepend_prefix?: string;
  ani_rewrite?: string;
}
